import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  enTranslation,
  enTranslationDocumentDesigner,
  TranslationDocumentDesignerType,
  TranslationProducts,
  TranslationType,
} from '@/Localization/LanguageKeys';
import { LANGUAGE_SELECT_OPTIONS_MAP } from '@/Localization/constants';
import {
  convertLocaleToLocalization,
  convertLocalizationToLocale,
  LocaleType,
  LocalizationType,
} from '@/Enums/LocalizationType'; // Error while importing from index.ts
import { Pair } from '@/Types';
import { CountryCode, LoadingType } from '@/Enums';
import { translationMapping } from '@/Mapping/TranslationMapping';

export type GlobalState = {
  loadingType: LoadingType;
  translation: TranslationType;
  translationDocumentDesigner: TranslationDocumentDesignerType;
  translationProducts: TranslationProducts;
  selectedLanguage: Pair<LocalizationType>;
  isConnectionLost: boolean;
  countriesUnderSanction: CountryCode[];
};

const browserLanguage =
  LANGUAGE_SELECT_OPTIONS_MAP[convertLocaleToLocalization(window.localStorage.getItem('lang') as LocaleType)] ??
  LANGUAGE_SELECT_OPTIONS_MAP[LocalizationType.enEN];

const initialState: GlobalState = {
  loadingType: LoadingType.None,
  translation: enTranslation,
  translationDocumentDesigner: enTranslationDocumentDesigner,
  translationProducts: {},
  selectedLanguage: browserLanguage,
  isConnectionLost: false,
  countriesUnderSanction: [],
};

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setTranslation(state, action: PayloadAction<TranslationType>) {
      state.translation = action.payload;
    },
    setLoadingType(state, action: PayloadAction<{ loadingType: LoadingType; url?: string }>) {
      state.loadingType = action.payload.loadingType;
    },
    setLanguage(state, action: PayloadAction<LocalizationType>) {
      state.selectedLanguage = { key: action.payload, value: action.payload };
    },
    setConnectionLost(state, action: PayloadAction<boolean>) {
      state.isConnectionLost = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('global/get-translation/fulfilled', (state, action: any) => {
        state.selectedLanguage = LANGUAGE_SELECT_OPTIONS_MAP[action.payload.localization];

        window.localStorage.setItem('lang', convertLocalizationToLocale(action.payload.localization));

        state.translation = translationMapping.shortModelsToType<TranslationType>(action.payload.translations);
      })
      .addCase('global/get-document-designer-translation/fulfilled', (state, action: any) => {
        state.translationDocumentDesigner = translationMapping.shortModelsToType<TranslationDocumentDesignerType>(
          action.payload.translations,
        );
      })
      .addCase('global/get-products-translation/fulfilled', (state, action: any) => {
        state.translationProducts = translationMapping.shortModelsToType<TranslationProducts>(
          action.payload.translations,
        );
      })
      .addCase('global/get-sanction-countries/fulfilled', (state, action: any) => {
        state.countriesUnderSanction = action.payload.sanctionCountries;
      });
  },
});

export const globalActions = globalSlice.actions;
export const globalReducer = globalSlice.reducer;
